import React, { useEffect, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import DialogContent from '@mui/material/DialogContent'
import { Stack, TextField, useMediaQuery, useTheme } from '@mui/material'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import Tooltip from '@mui/material/Tooltip'
import Input from '@mui/material/Input'
import Box from '@mui/material/Box'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import isAxiosError from 'shared/utils/helpers/isAxiosError'
import CustomDialog from 'shared/components/CustomDialog'
import CustomTable from 'shared/components/CustomTable'

import ImageComponent from 'shared/components/ImageComponent'
import { Add, Remove } from '@mui/icons-material'
import { SearchResponseType, ReflinkDataType, SearchProductType, AddProductDetails } from '../../types'
import { addProductService } from '../../services/addProductService'
import { StyledAvailabilityDot, StyledSpan } from '../../styles'
import { checkProductPriceService } from '../../services/checkProductPriceService'

interface Props {
   searchPhrase: string
   data?: SearchResponseType
   hideGrossPrices: boolean
   open: boolean
   updateData: (data: ReflinkDataType) => void
   handleClose: () => void
}
export default function SearchModal({ searchPhrase, data, open, hideGrossPrices, handleClose, updateData }: Props) {
   const { addNotification } = useNotificationContext()
   const [addToCartDetails, setAddToCartDetails] = useState<AddProductDetails>({
      quantity: 1,
      producerName: '',
      partName: '',
      netPrice: '',
      grossPrice: '',
      uuid: '',
      index: '',
      producerUuid: '',
   })
   const { t } = useTranslation(['CLIENT_CART', 'TRANSLATION'])
   const [isAdding, setIsAdding] = useState(false)
   const [isCheckingPrice, setIsCheckingPrice] = useState(false)
   const [multipleCheckedProducts, setMultipleCheckedProducts] = useState(false)
   const theme = useTheme()
   const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
   const fullScreen = useMediaQuery(theme.breakpoints.down('lg'))

   function foundResultsString(resultsCount: number) {
      if (resultsCount === 0) return 'notFound'
      return ''
   }

   function returnProductName(productName: string, productIndex: string) {
      if (productName) {
         if (productName.length > 100) {
            return `${productName.slice(0, 100)}...`
         }
         return productName
      }
      return productIndex
   }

   function returnAvailabilityDot(availability: number) {
      switch (true) {
         case availability === 0:
            return <StyledAvailabilityDot color="red" />
         case availability === 1:
            return <StyledAvailabilityDot color="orange" />
         case availability === -1:
            return <StyledAvailabilityDot color="gray" />
         case availability > 1:
            return <StyledAvailabilityDot color="green" />
         default:
            return null
      }
   }

   function returnName() {
      if (addToCartDetails.partName) {
         if (addToCartDetails.partName) {
            return addToCartDetails.partName.split(' ').slice(0, 2).join(' ')
         }
         return addToCartDetails.partName.length > 50
            ? addToCartDetails.partName.slice(0, 50)
            : addToCartDetails.partName
      }
      return null
   }

   function handleAddProduct() {
      const uuid = window.localStorage.getItem('uuid')
      const token = window.localStorage.getItem('reflink-verify-token')
      if (uuid && token) {
         setIsAdding(true)

         addProductService(uuid, token, addToCartDetails.uuid, addToCartDetails.quantity, addToCartDetails.index)
            .then((res) => {
               addNotification(t('CLIENT_CART:searchModal.productAdded'), 'success')
               if (res.data) {
                  updateData(res.data)
               }
               handleClose()
            })
            .catch((error) => {
               if (isAxiosError<ErrorType>(error)) {
                  if (error.response?.data.errors[0].code === 'errors.authentication.reflink-code-expired') {
                     addNotification(t('CLIENT_CART:expired'), 'error')
                     window.localStorage.removeItem('reflink-verify-token')
                  } else {
                     addNotification(
                        error.response?.data?.errors[0].code || t('CLIENT_CART:searchModal.productAddFailed'),
                        'error'
                     )
                  }
               } else {
                  addNotification(t('CLIENT_CART:searchModal.productAddFailed'), 'error')
               }
            })
            .finally(() => setIsAdding(false))
      }
   }

   function handleCheckProductPrice() {
      const uuid = window.localStorage.getItem('uuid')
      const token = window.localStorage.getItem('reflink-verify-token')
      setMultipleCheckedProducts(false)

      if (uuid && token) {
         setIsCheckingPrice(true)
         checkProductPriceService(uuid, token, addToCartDetails.uuid, addToCartDetails.quantity)
            .then((res) => {
               if (res.data) {
                  let selectedProduct
                  if (res.data.length > 1) {
                     setMultipleCheckedProducts(true)
                     selectedProduct = res.data.reduce((min, curr) =>
                        curr.netClientPrice < min.netClientPrice ? curr : min
                     )
                  } else {
                     setMultipleCheckedProducts(false)
                     selectedProduct = { ...res.data[0] }
                  }

                  setAddToCartDetails((prevState) => ({
                     ...prevState,
                     grossPrice: selectedProduct.formattedGrossClientPrice,
                     netPrice: selectedProduct.formattedNetClientPrice,
                  }))
               }
            })
            .catch((error) => {
               if (isAxiosError<ErrorType>(error)) {
                  if (error.response?.data.errors[0].code === 'errors.authentication.reflink-code-expired') {
                     addNotification(t('CLIENT_CART:expired'), 'error')
                     window.localStorage.removeItem('reflink-verify-token')
                  } else {
                     addNotification(
                        error.response?.data?.errors[0].code || t('CLIENT_CART:searchModal.productAddFailed'),
                        'error'
                     )
                  }
               } else {
                  addNotification(t('CLIENT_CART:searchModal.productAddFailed'), 'error')
               }
            })
            .finally(() => setIsCheckingPrice(false))
      }
   }

   function renderContent() {
      if (addToCartDetails.uuid) {
         return (
            <DialogContent>
               {multipleCheckedProducts && (
                  <Typography sx={{ maxWidth: '300px', m: '0 auto' }} textAlign="center" color="error">
                     {t('CLIENT_CART:searchModal.splittedProducts')}
                  </Typography>
               )}
               <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="space-between">
                  <div style={{ flex: 1 }}>
                     <Box
                        sx={{
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'center',
                           justifyContent: 'center',
                           pt: { lg: '30px', md: '30px', xs: '15px' },
                        }}
                     >
                        <Box sx={{ marginBottom: { lg: '55px', md: '55px', xs: '25px' } }}>
                           <ImageComponent
                              filename={addToCartDetails.producerName.toLowerCase()}
                              producerUuid={addToCartDetails.producerUuid}
                              producerName={addToCartDetails.producerName}
                              width="160px"
                              height="auto"
                           />
                        </Box>
                        <div style={{ display: 'flex', minWidth: 200, flexDirection: 'column', gap: 2 }}>
                           <div style={{ display: 'flex', gap: 2 }}>
                              <Typography variant="body1" color="text.secondary">
                                 {`${t('TRANSLATION:columnNames.partIndex')}: `}
                              </Typography>
                              <Typography variant="body1">{addToCartDetails.index}</Typography>
                           </div>
                           <div style={{ display: 'flex', gap: 2 }}>
                              <Typography variant="body1" color="text.secondary">
                                 {`${t('TRANSLATION:columnNames.productName')}: `}
                              </Typography>
                              <Typography variant="body1">{returnName()}</Typography>
                           </div>
                           <div style={{ display: 'flex', gap: 2 }}>
                              <Typography variant="body1" color="text.secondary">
                                 {`${t('TRANSLATION:columnNames.producer')}: `}
                              </Typography>
                              <Typography variant="body1">{addToCartDetails.producerName}</Typography>
                           </div>
                        </div>
                     </Box>
                  </div>
                  <Box sx={{ flex: 1, display: 'flex', gap: 2, flexDirection: 'column', minWidth: '150px' }}>
                     {!fullScreen ? (
                        <Box
                           sx={{
                              marginBottom: 3,
                              marginTop: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                           }}
                        >
                           <TextField
                              variant="standard"
                              label={t('TRANSLATION:columnNames.pickQuantity')}
                              disabled={isAdding}
                              fullWidth
                              type="Number"
                              onKeyDown={(e) => {
                                 if (e.key === 'Enter') {
                                    handleAddProduct()
                                 }
                              }}
                              value={addToCartDetails.quantity}
                              onChange={(e) =>
                                 setAddToCartDetails({ ...addToCartDetails, quantity: Number(e.target.value) })
                              }
                              slotProps={{
                                 htmlInput: { min: 1, max: 600 },
                              }}
                           />
                           <TextField
                              variant="standard"
                              label={t('TRANSLATION:columnNames.netPrice')}
                              disabled={isAdding}
                              fullWidth
                              value={addToCartDetails.netPrice ?? '-'}
                              slotProps={{
                                 htmlInput: { min: 1, max: 600 },
                              }}
                           />
                           {!hideGrossPrices && (
                              <TextField
                                 variant="standard"
                                 label={t('TRANSLATION:columnNames.grossPrice')}
                                 disabled={isAdding}
                                 fullWidth
                                 value={addToCartDetails.grossPrice ?? '-'}
                                 slotProps={{
                                    htmlInput: { min: 1, max: 600 },
                                 }}
                              />
                           )}
                        </Box>
                     ) : (
                        <Box
                           sx={{
                              marginBottom: 3,
                              marginTop: 2,
                              width: '100%',
                              display: 'flex',
                              flexDirection: { lg: 'row', md: 'column', xs: 'column' },
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: '24px',
                           }}
                        >
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <ContainedButton
                                 onClick={() => {
                                    if (addToCartDetails.quantity > 1) {
                                       setAddToCartDetails({
                                          ...addToCartDetails,
                                          quantity: addToCartDetails.quantity - 1,
                                       })
                                    }
                                 }}
                              >
                                 <Remove />
                              </ContainedButton>
                              <TextField
                                 sx={{
                                    minWidth: '100px',
                                 }}
                                 variant="standard"
                                 label={t('TRANSLATION:columnNames.pickQuantity')}
                                 disabled={isAdding}
                                 fullWidth
                                 type="Number"
                                 onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                       handleAddProduct()
                                    }
                                 }}
                                 value={addToCartDetails.quantity}
                                 onChange={(e) =>
                                    setAddToCartDetails({ ...addToCartDetails, quantity: Number(e.target.value) })
                                 }
                                 slotProps={{
                                    htmlInput: { min: 1, max: 600 },
                                 }}
                              />
                              <ContainedButton
                                 onClick={() => {
                                    if (addToCartDetails.quantity < 600) {
                                       setAddToCartDetails({
                                          ...addToCartDetails,
                                          quantity: addToCartDetails.quantity + 1,
                                       })
                                    }
                                 }}
                              >
                                 <Add />
                              </ContainedButton>
                           </Box>
                           <TextField
                              variant="standard"
                              label={t('TRANSLATION:columnNames.netPrice')}
                              disabled={isAdding}
                              fullWidth
                              value={addToCartDetails.netPrice ?? '-'}
                              slotProps={{
                                 htmlInput: { min: 1, max: 600 },
                              }}
                           />
                           {!hideGrossPrices && (
                              <TextField
                                 variant="standard"
                                 label={t('TRANSLATION:columnNames.grossPrice')}
                                 disabled={isAdding}
                                 fullWidth
                                 value={addToCartDetails.grossPrice ?? '-'}
                                 slotProps={{
                                    htmlInput: { min: 1, max: 600 },
                                 }}
                              />
                           )}
                        </Box>
                     )}
                  </Box>
               </Stack>
            </DialogContent>
         )
      }
      if (data) {
         return (
            <DialogContent>
               <div
                  style={{
                     margin: '0 auto',
                     display: 'flex',
                     flexDirection: data.resultsCount >= 250 || data.resultsCount === 0 ? 'column' : 'row',
                     gap: 5,
                  }}
               >
                  <Typography variant="body1" color="text.secondary">
                     {t('CLIENT_CART:searchedPhrase')}
                     <StyledSpan>{searchPhrase}</StyledSpan>
                  </Typography>
                  {data.resultsCount === 0 || data.resultsCount === 250 ? (
                     <Typography variant="h6" sx={{ color: 'red', textTransform: 'upperCase', margin: '0 auto' }}>
                        {t(`CLIENT_CART:${foundResultsString(data.resultsCount)}`)}
                     </Typography>
                  ) : (
                     <Typography variant="body1" sx={{ color: 'grayText' }}>
                        {t('CLIENT_CART:found')} <StyledSpan> {data ? data.resultsCount : 0} </StyledSpan>
                     </Typography>
                  )}
               </div>
               {data.products.length > 0 && (
                  <CustomTable
                     dependencies={[]}
                     rows={data.products}
                     onRowClick={(row: SearchProductType) => {
                        setAddToCartDetails({
                           quantity: 1,
                           index: row.index,
                           uuid: row.uuid,
                           producerUuid: row.producerUuid,
                           producerName: row.producerNameWithoutSpecialChars,
                           grossPrice: '',
                           netPrice: '',
                           partName: row.name,
                        })
                        setMultipleCheckedProducts(false)
                        setIsCheckingPrice(false)
                        setIsAdding(false)
                     }}
                     dataSize={data.products.length}
                     pagingVariants={[10, 25, 50]}
                     columns={[
                        {
                           name: isMobile ? 'indexAndName' : 'partIndex',
                           sortable: true,
                           mainOnMobile: true,
                           sortLabel: 'index',
                           sortType: 'string',
                           renderCell: (row: SearchProductType) =>
                              isMobile ? (
                                 <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <span>{row.index.length > 30 ? `${row.index.slice(0, 30)}...` : row.index}</span>
                                    <span>{row.name}</span>
                                 </Box>
                              ) : (
                                 <Box>{row.index.length > 30 ? `${row.index.slice(0, 30)}...` : row.index}</Box>
                              ),
                        },
                        {
                           name: 'productName',
                           sortable: true,
                           sortLabel: 'name',
                           sortType: 'string',
                           renderCell: (row: SearchProductType) => returnProductName(row.name, row.index),
                        },
                        {
                           name: 'producer',
                           mainOnMobile: true,
                           sortable: true,
                           sortLabel: 'producer',
                           sortType: 'string',
                           renderCell: (row: SearchProductType) => (
                              <Tooltip title={row.producer}>
                                 <ImageComponent
                                    producerUuid={row.producerUuid}
                                    height={isMobile ? '15px' : '20px'}
                                    filename={row.producerNameWithoutSpecialChars.toLowerCase()}
                                    producerName={row.producer}
                                 />
                              </Tooltip>
                           ),
                        },
                        {
                           name: 'availability',
                           sortable: true,
                           mainOnMobile: true,
                           sortLabel: 'availability',
                           sortType: 'number',
                           renderCell: (row: SearchProductType) => returnAvailabilityDot(row.availability),
                        },
                        {
                           name: '',
                           onlyOnMobile: true,
                           renderCell: (row: SearchProductType) => (
                              <ContainedButton
                                 variant="contained"
                                 onClick={() =>
                                    setAddToCartDetails({
                                       quantity: 1,
                                       index: row.index,
                                       uuid: row.uuid,
                                       producerUuid: row.producerUuid,
                                       producerName: row.producerNameWithoutSpecialChars,
                                       grossPrice: '',
                                       netPrice: '',
                                       partName: row.name,
                                    })
                                 }
                              >
                                 {t('TRANSLATION:add')}
                              </ContainedButton>
                           ),
                        },
                     ]}
                  />
               )}
            </DialogContent>
         )
      }
      return (
         <DialogContent sx={{ width: '100%', textAlign: 'center' }}>
            <CircularProgress />
         </DialogContent>
      )
   }

   useEffect(() => {
      setAddToCartDetails({
         quantity: 1,
         uuid: '',
         index: '',
         partName: '',
         grossPrice: '',
         producerUuid: '',
         netPrice: '',
         producerName: '',
      })
   }, [open])

   return (
      <CustomDialog
         isLoading={!open || !addToCartDetails}
         maxWidth="xl"
         fullScreen={isMobile}
         fullWidth={!addToCartDetails.uuid}
         open={open}
         onClose={handleClose}
         content={
            <>
               <DialogTitle>{t('CLIENT_CART:searchModal.searchResults')}</DialogTitle>
               {renderContent()}
               <DialogActions>
                  {addToCartDetails.uuid ? (
                     <>
                        <OutlinedButton
                           disabled={isAdding}
                           variant="outlined"
                           onClick={() =>
                              setAddToCartDetails({
                                 quantity: 1,
                                 uuid: '',
                                 producerName: '',
                                 partName: '',
                                 producerUuid: '',
                                 grossPrice: '',
                                 netPrice: '',
                                 index: '',
                              })
                           }
                        >
                           {t('TRANSLATION:cancel')}
                        </OutlinedButton>
                        <ContainedButton
                           disabled={isCheckingPrice || isAdding}
                           variant="contained"
                           onClick={handleCheckProductPrice}
                        >
                           {t('TRANSLATION:checkPrice')}
                        </ContainedButton>
                        <ContainedButton
                           disabled={isCheckingPrice || isAdding}
                           variant="contained"
                           onClick={handleAddProduct}
                        >
                           {t('TRANSLATION:addToCart')}
                        </ContainedButton>
                     </>
                  ) : (
                     <ContainedButton
                        disabled={isCheckingPrice || isAdding}
                        sx={{ margin: '0 auto' }}
                        variant="contained"
                        onClick={handleClose}
                     >
                        {t('CLIENT_CART:close')}
                     </ContainedButton>
                  )}
               </DialogActions>
            </>
         }
      />
   )
}
